// ═══════════════════════════════════════════════════════════════
// Messages.jsx — XC-MSG platform component (Path Z)
// Path: src/components/Messages.jsx
//
// S6.B (May 19, 2026) — NEW FILE. First production consumer of XcFilterBar
//                       with live filter wiring. Reads flat top-level
//                       /messages and derives threads client-side per
//                       S6 close handoff §6c.
//
// ARCHITECTURE — Path Z (AS-S6-001, approved Chris May 19 2026)
//   System of record = flat /messages collection. /messageThreads is
//   reserved for FUTURE thread-level aggregation (XC-TRIG at S11; AI-04
//   at S12). At S6 close /messageThreads remains empty by design.
//
//   This component:
//     1. Reads /messages where clientId == workspaceId orderBy createdAt desc
//     2. Groups messages into threads client-side by (clientId, phone-pair)
//     3. Derives 4-status vocabulary per §6c rules
//     4. Renders sandbox v7.2 xcMessages view (lines 7372–7440) shape
//     5. Compose → httpsCallable("sendSms") with auth.uid
//     6. AI-01 draft button → httpsCallable("draftGuestComm")
//
// CANONICAL SCHEMA: see S6_Section_6_Canonical_Schemas.docx §6a + §6c.
//   /messages fields used: clientId, direction, channel, toPhone, fromPhone,
//   body, recipientUid, senderUid, workOrderId, readBy[], createdAt.
//
// STATUS DERIVATION (per §6c):
//   unread  — latest msg direction='inbound' AND current uid NOT in readBy[]
//   replied — both directions present AND latest msg is read
//   auto    — has workOrderId AND no human senderUid in thread
//   review  — has senderUid in AI_04_SENDERS list
//   Precedence: unread > review > auto > replied
//
// PROPS
//   workspace  — workspace object with workspaceId (= clientId)
//   roles      — user's roles array in this workspace
//   roleLabel  — display label for current role
//   mockMode   — if true, render hardcoded 6-thread mock array
//                (for ?xcmsg-smoke=1 harness)
//
// AS REFERENCES
//   AS-S6-001 — Path Z (flat /messages SoR)
//   AS-S6-003 — /messages rule block in v3.4 (PM+ read all workspace)
//   AS-S6-008 — channel='sms' only at S6; Email chip greyed
//   AS-S6-010 — participantRole singular vocabulary
//   AS-S6-013 — status derivation rules client-side
//   AS-S6-014 — composite index on (clientId ASC, createdAt DESC) needed
//   AS-S4-007 — closure verified via Maintain.jsx retarget
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  collection, query, where, orderBy, onSnapshot, doc, updateDoc, arrayUnion,
} from 'firebase/firestore';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase/config';
import XcFilterBar, { useFilterState } from './xcFilterBar';

// ── Constants from §6a/§6c ───────────────────────────────────────────────
// AI-04 system sender list. Threads with any message senderUid in this list
// derive status='review' per §6c. Initial S6 list is one entry; grows as
// AI agents come online at S12.
const AI_04_SENDERS = ['ai-04'];

// System (non-human) sender UIDs for auto-thread detection per §6c.
// Used in combination with workOrderId presence to derive status='auto'.
const SYSTEM_SENDERS = ['system', null];

// Status pill colors from sandbox v7.2 xcMessages (lines 7415–7416).
const STATUS_COLORS = {
  unread:  { color: '#7A1F15', bg: '#FDEEEC' },
  replied: { color: '#0A5C42', bg: '#E8F7F2' },
  auto:    { color: '#3B2566', bg: '#F1EBFA' },
  review:  { color: '#7A4A08', bg: '#FDF3E3' },
};

// Mock thread set for ?xcmsg-smoke=1 harness. Each entry mirrors the
// shape of a derived thread (not a raw /messages doc). Six entries
// exercise all four statuses + range of participant roles per sandbox
// OPR_THREADS (lines 3851–3857) + xcMessages threadsByStage (line 7376).
const MOCK_THREADS = [
  {
    threadKey: 'mock-1',
    phonePair: '+13305551111',
    displayName: 'Monica Hinton',
    participantRole: 'tenant',
    subject: 'Is the rent going up in June?',
    lastMessagePreview: 'Is the rent going up in June?',
    lastMessageAt: '1h',
    status: 'unread',
    messageCount: 3,
  },
  {
    threadKey: 'mock-2',
    phonePair: '+13305552222',
    displayName: 'Seth · EverGuard',
    participantRole: 'vendor',
    subject: 'WO-001 Make-Ready paint done',
    lastMessagePreview: 'Paint done — carpet Thurs',
    lastMessageAt: '3h',
    status: 'replied',
    messageCount: 5,
  },
  {
    threadKey: 'mock-3',
    phonePair: '+13305553333',
    displayName: 'Larry Brown · PM',
    participantRole: 'pm',
    subject: 'April collections update',
    lastMessagePreview: '9 of 10 collected — Quinn late',
    lastMessageAt: '1d',
    status: 'replied',
    messageCount: 2,
  },
  {
    threadKey: 'mock-4',
    phonePair: '+13305554444',
    displayName: 'XC-TRIG auto',
    participantRole: 'system',
    subject: 'A-1 Unit 7 RTU condition <40 triggered',
    lastMessagePreview: 'Auto-fire to maintenance — vendor dispatched',
    lastMessageAt: '5d',
    status: 'auto',
    messageCount: 1,
  },
  {
    threadKey: 'mock-5',
    phonePair: '+13305555555',
    displayName: 'AI-04 (system)',
    participantRole: 'ai-04',
    subject: 'Sell/Hold analysis · Potters NNN',
    lastMessagePreview: 'Draft memo ready for review',
    lastMessageAt: '2h',
    status: 'review',
    messageCount: 1,
  },
  {
    threadKey: 'mock-6',
    phonePair: '+13305556666',
    displayName: 'BBow Spa Pros',
    participantRole: 'vendor',
    subject: 'Retriever Ridge spa filter ETA',
    lastMessagePreview: 'Friday delivery confirmed',
    lastMessageAt: '4h',
    status: 'replied',
    messageCount: 4,
  },
];

// ── Helpers ──────────────────────────────────────────────────────────────

/**
 * Build the phone-pair grouping key from a message doc. Phone-pair is the
 * sorted unordered set of (toPhone, fromPhone) — two messages between
 * numbers A and B belong to the same thread regardless of direction. Per
 * §6c thread identity rule.
 */
function phonePairKey(msg) {
  const pair = [msg.toPhone || '', msg.fromPhone || ''].filter(Boolean).sort();
  return pair.join('|');
}

/**
 * Truncate text to N chars with ellipsis. Used for subject/preview
 * synthesis per §6c.
 */
function truncate(s, n) {
  if (!s) return '';
  if (s.length <= n) return s;
  return s.substring(0, n) + '…';
}

/**
 * Format a timestamp as a relative-time short string ("1h", "3d", "Apr 5").
 */
function fmtTime(ts) {
  if (!ts) return '—';
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(d.getTime())) return '—';
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Group /messages docs into derived threads per §6c rules.
 * Returns array of thread objects with computed status + KPI inputs.
 */
function deriveThreads(messages, currentUid) {
  // Group by (clientId, phonePair) — though clientId is already filtered
  // by the parent query, include it in the key for future multi-workspace
  // aggregation if needed.
  const groups = {};
  messages.forEach((msg) => {
    const key = phonePairKey(msg);
    if (!key) return; // Skip messages with no phone info
    if (!groups[key]) groups[key] = [];
    groups[key].push(msg);
  });

  // Build derived threads
  return Object.entries(groups).map(([phonePair, msgs]) => {
    // Sort messages oldest → newest for derivation logic
    msgs.sort((a, b) => {
      const at = a.createdAt?.toDate?.()?.getTime?.() || 0;
      const bt = b.createdAt?.toDate?.()?.getTime?.() || 0;
      return at - bt;
    });
    const latest = msgs[msgs.length - 1];

    // Resolve phone-to-participant display (no users.phone lookup in S6.B
    // first pass — show phone literal). Future enhancement: hydrate
    // user docs by phone in a second query and merge.
    const otherPhone = (latest.toPhone === latest.fromPhone)
      ? latest.toPhone
      : [latest.toPhone, latest.fromPhone].filter(Boolean).find(p => p !== latest.toPhone) || latest.fromPhone;
    const displayName = otherPhone || 'Unknown';

    // Status derivation per §6c with precedence: unread > review > auto > replied
    const hasInbound = msgs.some(m => m.direction === 'inbound');
    const hasOutbound = msgs.some(m => m.direction === 'outbound');
    const latestUnread = latest.direction === 'inbound' &&
                         !(Array.isArray(latest.readBy) && latest.readBy.includes(currentUid));
    const hasReviewSender = msgs.some(m => AI_04_SENDERS.includes(m.senderUid));
    const hasWorkOrderAuto = msgs.some(m =>
      m.workOrderId && SYSTEM_SENDERS.includes(m.senderUid)
    );

    let status;
    if (latestUnread) status = 'unread';
    else if (hasReviewSender) status = 'review';
    else if (hasWorkOrderAuto && !msgs.some(m => m.senderUid && !SYSTEM_SENDERS.includes(m.senderUid))) status = 'auto';
    else if (hasInbound && hasOutbound) status = 'replied';
    else status = 'replied'; // Default per §6c test case 2

    return {
      threadKey: phonePair,
      phonePair,
      displayName,
      participantRole: null, // Derived from users.phone lookup — deferred
      subject: synthesizeSubject(latest, msgs, displayName),
      lastMessagePreview: truncate(latest.body, 80),
      lastMessageAt: latest.createdAt,
      latestMessageId: latest.id,
      status,
      messageCount: msgs.length,
      channel: latest.channel || 'sms',
      workOrderId: latest.workOrderId || null,
    };
  }).sort((a, b) => {
    // Newest threads first
    const at = a.lastMessageAt?.toDate?.()?.getTime?.() || 0;
    const bt = b.lastMessageAt?.toDate?.()?.getTime?.() || 0;
    return bt - at;
  });
}

/**
 * Synthesize a thread subject from message context per S6 close handoff §6c
 * subject derivation rules. Subject is a thread-LEVEL concept distinct from
 * the latest message body (preview). Priority:
 *   1. workOrderId present → "WO {short-id}"
 *   2. AI-04 system sender → "AI review memo"
 *   3. Otherwise → "Conversation with {other-party-phone-or-name}"
 *
 * Once /messageThreads docs exist (post-S11/S12), Messages.jsx will prefer
 * the explicit subject field on those docs and fall back to synthesis only
 * for threads without explicit metadata. AS-S6-FUT-006.
 */
function synthesizeSubject(latest, msgs, displayName) {
  // Priority 1: workOrder context — any msg in thread carries workOrderId
  const woMsg = msgs.find((m) => !!m.workOrderId);
  if (woMsg) {
    // Strip common prefixes for readability ("wo-seed-rop-paint-001" → "rop-paint-001")
    const short = woMsg.workOrderId
      .replace(/^wo-seed-/, '')
      .replace(/^wo-/, '');
    return `WO ${short}`;
  }
  // Priority 2: AI-04 review thread — any msg in thread has AI-04 senderUid
  if (msgs.some((m) => AI_04_SENDERS.includes(m.senderUid))) {
    return 'AI review memo';
  }
  // Priority 3: phone-based fallback — name or phone of other party
  return `Conversation with ${displayName || 'unknown'}`;
}



export default function Messages({ workspace, roles, roleLabel, mockMode = false }) {
  const auth = getAuth();
  const currentUid = auth.currentUser?.uid || null;
  const clientId = workspace?.workspaceId || workspace?.clientId || null;

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(!mockMode);
  const [error, setError] = useState(null);
  const [showCompose, setShowCompose] = useState(false);
  const [composeBody, setComposeBody] = useState('');
  const [composeRecipientUid, setComposeRecipientUid] = useState('');
  const [composeBusy, setComposeBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [filterValues] = useFilterState('xc-messages');

  // ── Live read of /messages (skipped in mockMode) ───────────────────────
  useEffect(() => {
    if (mockMode || !clientId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    let unsub;
    try {
      const q = query(
        collection(db, 'messages'),
        where('clientId', '==', clientId),
        orderBy('createdAt', 'desc')
      );
      unsub = onSnapshot(
        q,
        (snap) => {
          setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
          setLoading(false);
        },
        (err) => {
          // AS-S6-014: composite index may be missing. Soft-fallback retry
          // without orderBy (same pattern as S5.B Documents.jsx).
          console.warn('[Messages] orderBy query failed — retrying unordered:', err.message);
          try {
            const fallbackQ = query(
              collection(db, 'messages'),
              where('clientId', '==', clientId)
            );
            const fallbackUnsub = onSnapshot(
              fallbackQ,
              (snap) => {
                // Client-side sort since orderBy unavailable
                const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                list.sort((a, b) => {
                  const at = a.createdAt?.toDate?.()?.getTime?.() || 0;
                  const bt = b.createdAt?.toDate?.()?.getTime?.() || 0;
                  return bt - at;
                });
                setMessages(list);
                setLoading(false);
              },
              (err2) => {
                console.warn('[Messages] fallback also failed:', err2.message);
                setError(err2.message);
                setLoading(false);
              }
            );
            unsub = fallbackUnsub;
          } catch (err2) {
            setError(err2.message);
            setLoading(false);
          }
        }
      );
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }

    return () => { if (unsub) unsub(); };
  }, [clientId, mockMode]);

  // ── Derive threads (mock or live) ──────────────────────────────────────
  const threads = useMemo(() => {
    if (mockMode) return MOCK_THREADS;
    return deriveThreads(messages, currentUid);
  }, [messages, currentUid, mockMode]);

  // ── Apply filter chips ─────────────────────────────────────────────────
  const filteredThreads = useMemo(() => {
    return threads.filter((t) => {
      // State filter: All / Unread / Read / Replied
      const stateF = filterValues?.state;
      if (stateF && stateF !== 'All') {
        if (stateF === 'Unread' && t.status !== 'unread') return false;
        if (stateF === 'Read' && t.status === 'unread') return false;
        if (stateF === 'Replied' && t.status !== 'replied') return false;
      }
      // Channel filter: All / SMS / Email (Email greyed per AS-S6-008)
      const chF = filterValues?.channel;
      if (chF && chF !== 'All') {
        if (chF === 'SMS' && t.channel !== 'sms') return false;
        if (chF === 'Email' && t.channel !== 'email') return false;
      }
      return true;
    });
  }, [threads, filterValues]);

  // ── KPIs per §6c ───────────────────────────────────────────────────────
  const kpis = useMemo(() => ({
    total:   threads.length,
    unread:  threads.filter(t => t.status === 'unread').length,
    auto:    threads.filter(t => t.status === 'auto').length,
    review:  threads.filter(t => t.status === 'review').length,
  }), [threads]);

  // ── Compose flow — calls httpsCallable("sendSms") ──────────────────────
  const handleCompose = useCallback(async () => {
    if (mockMode) {
      setToastMsg('Mock mode — compose is not wired.');
      setShowCompose(false);
      return;
    }
    if (!composeRecipientUid.trim() || !composeBody.trim()) {
      setToastMsg('Recipient UID and body are required.');
      return;
    }
    setComposeBusy(true);
    try {
      const fn = httpsCallable(getFunctions(), 'sendSms');
      const result = await fn({
        recipientUid: composeRecipientUid.trim(),
        clientId,
        body: composeBody.trim(),
      });
      setToastMsg(`SMS sent · SID ${result.data?.twilioSid || 'unknown'}`);
      setComposeBody('');
      setComposeRecipientUid('');
      setShowCompose(false);
    } catch (err) {
      // Surface canonical CF error codes
      const code = err.code || '';
      if (code === 'functions/permission-denied') {
        setToastMsg('Permission denied — PM+ role required to send SMS.');
      } else if (code === 'functions/failed-precondition') {
        setToastMsg(`Cannot send: ${err.message}`);
      } else if (code === 'functions/not-found') {
        setToastMsg('Recipient user not found.');
      } else {
        setToastMsg(`Send failed: ${err.message}`);
      }
    } finally {
      setComposeBusy(false);
    }
  }, [composeRecipientUid, composeBody, clientId, mockMode]);

  // ── AI-01 draft assist — calls httpsCallable("draftGuestComm") ─────────
  const handleAiDraft = useCallback(async () => {
    if (mockMode) {
      setToastMsg('Mock mode — AI draft is not wired.');
      return;
    }
    setAiBusy(true);
    try {
      const fn = httpsCallable(getFunctions(), 'draftGuestComm');
      const result = await fn({
        clientId,
        context: 'compose-new',
        hint: composeBody || 'Friendly reminder',
      });
      const draft = result.data?.draft || result.data?.body || '';
      if (draft) {
        setComposeBody(draft);
        setToastMsg('AI-01 draft inserted — review before sending.');
      } else {
        setToastMsg('AI-01 returned empty draft.');
      }
    } catch (err) {
      setToastMsg(`AI draft failed: ${err.message}`);
    } finally {
      setAiBusy(false);
    }
  }, [composeBody, clientId, mockMode]);

  // ── Mark a thread's latest inbound msg as read ─────────────────────────
  const handleMarkRead = useCallback(async (thread) => {
    if (mockMode || !thread.latestMessageId || !currentUid) return;
    try {
      await updateDoc(doc(db, 'messages', thread.latestMessageId), {
        readBy: arrayUnion(currentUid),
      });
    } catch (err) {
      console.warn('[Messages] mark-read failed:', err.message);
    }
  }, [mockMode, currentUid]);

  // ── Auto-dismiss toast ─────────────────────────────────────────────────
  useEffect(() => {
    if (!toastMsg) return;
    const t = setTimeout(() => setToastMsg(''), 3000);
    return () => clearTimeout(t);
  }, [toastMsg]);

  // ── Loading + error states ─────────────────────────────────────────────
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingState}>Loading messages…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorState}>
          <strong>Could not load messages.</strong>
          <div style={{ marginTop: 6, fontSize: 12 }}>{error}</div>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div style={styles.container}>
      {/* Banner */}
      <div style={styles.banner}>
        <div>
          <span style={styles.bannerTitle}>Messages</span>
          <span style={styles.bannerTag}>XC-MSG</span>
        </div>
        <div style={styles.bannerSub}>
          Threaded comms · Twilio SMS · auto-fires on triggers · same UI all stages
        </div>
      </div>

      {/* Page head */}
      <div style={styles.pageHead}>
        <div>
          <h1 style={styles.pageTitle}>Messages</h1>
          <div style={styles.pageSub}>
            {kpis.total} thread{kpis.total !== 1 ? 's' : ''} ·
            {' '}{kpis.unread} unread · {kpis.auto} auto-fired · {kpis.review} AI drafts
          </div>
        </div>
        <div style={styles.pageActions}>
          <button
            type="button"
            style={styles.btnGhost}
            onClick={handleAiDraft}
            disabled={aiBusy}
          >
            {aiBusy ? 'AI drafting…' : '✨ AI Draft'}
          </button>
          <button
            type="button"
            style={styles.btnPrimary}
            onClick={() => setShowCompose(true)}
          >
            + Compose
          </button>
        </div>
      </div>

      {/* Filter bar — XcFilterBar (S4 component, first live consumer) */}
      <XcFilterBar viewId="xc-messages" />

      {/* KPI grid — matches sandbox xcMessages lines 7414–7419 */}
      <div style={styles.kpiGrid}>
        <div style={{ ...styles.kpiCard, ...styles.kpiAccent }}>
          <div style={styles.kpiLabel}>Threads</div>
          <div style={styles.kpiValue}>{kpis.total}</div>
          <div style={styles.kpiSub}>stage filter</div>
        </div>
        <div style={{ ...styles.kpiCard, ...(kpis.unread > 0 ? styles.kpiWarn : {}) }}>
          <div style={styles.kpiLabel}>Unread</div>
          <div style={styles.kpiValue}>{kpis.unread}</div>
          <div style={styles.kpiSub}>awaiting response</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={styles.kpiLabel}>Auto-fired</div>
          <div style={styles.kpiValue}>{kpis.auto}</div>
          <div style={styles.kpiSub}>from XC-TRIG events</div>
        </div>
        <div style={{ ...styles.kpiCard, ...styles.kpiGood }}>
          <div style={styles.kpiLabel}>AI drafts</div>
          <div style={styles.kpiValue}>{kpis.review}</div>
          <div style={styles.kpiSub}>pending review</div>
        </div>
      </div>

      {/* Thread list */}
      <div style={styles.card}>
        <div style={styles.cardHead}>
          <div>
            <div style={styles.cardTitle}>Threads</div>
            <div style={styles.cardSub}>SMS · AI-01 draft assist on every reply</div>
          </div>
          {mockMode && (
            <div style={styles.mockBadge}>MOCK MODE · ?xcmsg-smoke=1</div>
          )}
        </div>

        {filteredThreads.length === 0 ? (
          <div style={styles.emptyState}>
            {threads.length === 0
              ? 'No threads yet. Click Compose to start one.'
              : 'No threads match the current filter.'}
          </div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>From</th>
                <th style={styles.th}>Subject</th>
                <th style={styles.th}>Last</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredThreads.map((t) => {
                const sc = STATUS_COLORS[t.status] || STATUS_COLORS.replied;
                return (
                  <tr key={t.threadKey} style={styles.tableRow}>
                    <td style={styles.td}>
                      <div style={styles.fromName}>{t.displayName}</div>
                      {t.participantRole && (
                        <div style={styles.fromRole}>{t.participantRole}</div>
                      )}
                    </td>
                    <td style={styles.td}>
                      <div style={styles.subject}>{t.subject || '(no subject)'}</div>
                      <div style={styles.preview}>{t.lastMessagePreview}</div>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.timeText}>{
                        typeof t.lastMessageAt === 'string'
                          ? t.lastMessageAt
                          : fmtTime(t.lastMessageAt)
                      }</span>
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.statusPill, color: sc.color, background: sc.bg }}>
                        {t.status}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <button
                        type="button"
                        style={styles.btnGhostSm}
                        onClick={() => {
                          if (t.status === 'unread') handleMarkRead(t);
                          setToastMsg(`Open thread — full detail panel coming in a future release.`);
                        }}
                      >
                        Open →
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Compose modal */}
      {showCompose && (
        <div style={styles.modalBackdrop} onClick={() => !composeBusy && setShowCompose(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHead}>
              <div style={styles.modalTitle}>Compose SMS</div>
              <button
                type="button"
                style={styles.modalClose}
                onClick={() => !composeBusy && setShowCompose(false)}
              >
                ×
              </button>
            </div>
            <div style={styles.modalBody}>
              <label style={styles.label}>Recipient UID</label>
              <input
                type="text"
                style={styles.input}
                placeholder="User UID (looked up by sendSms)"
                value={composeRecipientUid}
                onChange={(e) => setComposeRecipientUid(e.target.value)}
                disabled={composeBusy}
              />
              <label style={styles.label}>Message body</label>
              <textarea
                style={styles.textarea}
                placeholder="Type your message…"
                value={composeBody}
                onChange={(e) => setComposeBody(e.target.value)}
                rows={5}
                maxLength={1600}
                disabled={composeBusy}
              />
              <div style={styles.charCount}>
                {composeBody.length} / 1600 chars
              </div>
            </div>
            <div style={styles.modalActions}>
              <button
                type="button"
                style={styles.btnGhost}
                onClick={handleAiDraft}
                disabled={aiBusy || composeBusy}
              >
                {aiBusy ? 'AI drafting…' : '✨ AI Draft'}
              </button>
              <button
                type="button"
                style={styles.btnGhost}
                onClick={() => setShowCompose(false)}
                disabled={composeBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                style={styles.btnPrimary}
                onClick={handleCompose}
                disabled={composeBusy || !composeBody.trim() || !composeRecipientUid.trim()}
              >
                {composeBusy ? 'Sending…' : 'Send SMS'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toastMsg && (
        <div style={styles.toast}>{toastMsg}</div>
      )}
    </div>
  );
}

// ── Inline styles (Arial, verticalstack cyan #00B8D0 / navy #003060) ────

const styles = {
  container: {
    maxWidth: 1100,
    margin: '0 auto',
    padding: '0 20px',
    fontFamily: 'Arial, sans-serif',
    color: '#0F172A',
  },
  banner: {
    background: '#E6F4F8',
    border: '1px solid rgba(0, 184, 208, 0.25)',
    borderRadius: 8,
    padding: '12px 18px',
    marginBottom: 18,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
  },
  bannerTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#003060',
  },
  bannerTag: {
    display: 'inline-block',
    marginLeft: 8,
    padding: '2px 8px',
    fontSize: 10,
    fontWeight: 700,
    color: '#003060',
    background: 'rgba(0, 184, 208, 0.18)',
    borderRadius: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  bannerSub: {
    fontSize: 12,
    color: '#5A6478',
    fontFamily: 'Consolas, "Courier New", monospace',
  },
  pageHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 14,
    flexWrap: 'wrap',
    gap: 12,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: '#003060',
    margin: 0,
    marginBottom: 4,
  },
  pageSub: {
    fontSize: 13,
    color: '#5A6478',
  },
  pageActions: {
    display: 'flex',
    gap: 8,
  },
  btnPrimary: {
    background: '#00B8D0',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 8,
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  btnGhost: {
    background: '#FFFFFF',
    color: '#003060',
    border: '1px solid #003060',
    borderRadius: 8,
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  btnGhostSm: {
    background: '#FFFFFF',
    color: '#003060',
    border: '1px solid #CBD5E1',
    borderRadius: 6,
    padding: '5px 11px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 12,
    marginBottom: 18,
  },
  kpiCard: {
    background: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    padding: '14px 16px',
  },
  kpiAccent: {
    borderColor: '#00B8D0',
    background: '#F0FBFD',
  },
  kpiWarn: {
    borderColor: '#FCA5A5',
    background: '#FEF2F2',
  },
  kpiGood: {
    borderColor: '#86EFAC',
    background: '#F0FDF4',
  },
  kpiLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: '#5A6478',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 4,
  },
  kpiValue: {
    fontSize: 24,
    fontWeight: 800,
    color: '#003060',
    marginBottom: 2,
  },
  kpiSub: {
    fontSize: 11,
    color: '#9CA3AF',
  },
  card: {
    background: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: 10,
    padding: 0,
    overflow: 'hidden',
  },
  cardHead: {
    padding: '14px 18px',
    borderBottom: '1px solid #F1F5F9',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#003060',
  },
  cardSub: {
    fontSize: 12,
    color: '#5A6478',
    marginTop: 2,
  },
  mockBadge: {
    background: '#FEF3C7',
    color: '#92400E',
    border: '1px solid #FBBF24',
    borderRadius: 6,
    padding: '4px 10px',
    fontSize: 11,
    fontWeight: 700,
    fontFamily: 'Consolas, "Courier New", monospace',
    letterSpacing: 0.3,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '10px 14px',
    fontSize: 11,
    fontWeight: 700,
    color: '#5A6478',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    borderBottom: '1px solid #E5E7EB',
    background: '#F8FAFC',
  },
  tableRow: {
    borderBottom: '1px solid #F1F5F9',
  },
  td: {
    padding: '12px 14px',
    fontSize: 13,
    color: '#0F172A',
    verticalAlign: 'top',
  },
  fromName: {
    fontWeight: 600,
    fontSize: 13,
  },
  fromRole: {
    fontSize: 10,
    fontWeight: 600,
    color: '#5A6478',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    marginTop: 2,
  },
  subject: {
    fontWeight: 500,
    fontSize: 13,
    color: '#0F172A',
    marginBottom: 2,
  },
  preview: {
    fontSize: 12,
    color: '#5A6478',
  },
  timeText: {
    fontFamily: 'Consolas, "Courier New", monospace',
    fontSize: 11,
    color: '#5A6478',
  },
  statusPill: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 700,
    fontFamily: 'Consolas, "Courier New", monospace',
    textTransform: 'lowercase',
  },
  emptyState: {
    padding: '40px 20px',
    textAlign: 'center',
    color: '#9CA3AF',
    fontSize: 14,
  },
  loadingState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#9CA3AF',
    fontSize: 14,
  },
  errorState: {
    padding: '40px 20px',
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    color: '#991B1B',
    borderRadius: 10,
    fontSize: 13,
  },
  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#FFFFFF',
    borderRadius: 12,
    width: 'min(560px, 92%)',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.25)',
  },
  modalHead: {
    padding: '16px 20px',
    borderBottom: '1px solid #F1F5F9',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#003060',
  },
  modalClose: {
    background: 'transparent',
    border: 'none',
    fontSize: 24,
    color: '#5A6478',
    cursor: 'pointer',
    lineHeight: 1,
    padding: 0,
    width: 28,
    height: 28,
  },
  modalBody: {
    padding: '16px 20px',
  },
  label: {
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    color: '#5A6478',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 4,
    marginTop: 10,
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    fontSize: 13,
    border: '1px solid #CBD5E1',
    borderRadius: 6,
    fontFamily: 'inherit',
    color: '#0F172A',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '8px 12px',
    fontSize: 13,
    border: '1px solid #CBD5E1',
    borderRadius: 6,
    fontFamily: 'inherit',
    color: '#0F172A',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  charCount: {
    fontSize: 11,
    color: '#9CA3AF',
    textAlign: 'right',
    marginTop: 4,
    fontFamily: 'Consolas, "Courier New", monospace',
  },
  modalActions: {
    padding: '12px 20px 18px',
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
  },
  toast: {
    position: 'fixed',
    bottom: 24,
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#003060',
    color: '#FFFFFF',
    padding: '10px 18px',
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 500,
    boxShadow: '0 10px 30px rgba(15,23,42,0.18)',
    zIndex: 1100,
    maxWidth: '90%',
    textAlign: 'center',
  },
};
